import { PureComponent } from 'react';
import PropTypes from 'prop-types';
import qs from 'qs';
import { connect } from 'react-redux';

import { getDisplayName } from '../../utils';
import { appointmentDraftActions, journeyStateActions, tokenApi } from '../../api';
import { getTokenFromStorage, restoreOriginalTokenFromTemporary } from './persist-token.utils';
import { apiCallFinished, countedApiCallFinished } from '../loading-spinner';
import { containsMessage } from '../layout/helpers';
import { errorTypes } from '../messages';
import { isEditAppointmentPage } from '~/routes/helpers';

// @TODO add HOC for manage loading state

export const persistToken = (Component) =>
  class extends PureComponent {
    static displayName = `PersistToken(${getDisplayName(Component)})`;

    static propTypes = {
      editMode: PropTypes.bool.isRequired,
      draft: PropTypes.shape({}),
      location: PropTypes.shape({
        search: PropTypes.string,
      }).isRequired,
      hasInternalServerError: PropTypes.bool,
      dispatchRestoreAppointmentDraftSelectedData: PropTypes.func.isRequired,
      dispatchRestoreJourneyStateSelectedData: PropTypes.func.isRequired,
      dispatchApiCallFinished: PropTypes.func.isRequired,
      dispatchSaveDraftAppointmentType: PropTypes.func.isRequired,
      dispatchCountedApiCallFinished: PropTypes.func.isRequired,
      tryAgainClicked: PropTypes.bool.isRequired,
      storedAppointmentTypeId: PropTypes.string.isRequired,
    };

    static defaultProps = {
      hasInternalServerError: false,
      draft: null,
    };

    state = {
      isSelectedDataRestored: false,
    };

    componentDidMount() {
      const {
        editMode,
        location: { search, pathname },

        dispatchRestoreAppointmentDraftSelectedData,
        dispatchApiCallFinished,
        dispatchCountedApiCallFinished,
        dispatchSaveDraftAppointmentType,
        dispatchRestoreJourneyStateSelectedData,
        storedAppointmentTypeId,
        tryAgainClicked,
      } = this.props;

      if (!editMode || !isEditAppointmentPage(pathname)) {
        restoreOriginalTokenFromTemporary();
      }

      dispatchApiCallFinished(false);

      if (!tryAgainClicked) {
        const { appointmentTypeId } = qs.parse(search, { ignoreQueryPrefix: true });
        const tokenFromStorage = getTokenFromStorage();
        if (appointmentTypeId && tokenFromStorage) {
          dispatchCountedApiCallFinished(false);
          dispatchSaveDraftAppointmentType(appointmentTypeId)
            .then(() => dispatchRestoreAppointmentDraftSelectedData(getTokenFromStorage()))
            .then(() => dispatchRestoreJourneyStateSelectedData(getTokenFromStorage()))
            .then(() => dispatchCountedApiCallFinished(true))
            .then(
              () => this.setState({ isSelectedDataRestored: true }),
              () => this.setState({ isSelectedDataRestored: true }),
            );
        } else if (!editMode) {
          const tokenPromise = tokenFromStorage
            ? Promise.resolve(tokenFromStorage)
            : tokenApi.getInitialToken();
          tokenPromise.then((token) => {
            dispatchRestoreAppointmentDraftSelectedData(token)
              .then(() => dispatchRestoreJourneyStateSelectedData(token))
              .then(() => this.setState({ isSelectedDataRestored: true }));
          });
        } else if (editMode && storedAppointmentTypeId) {
          this.setState({ isSelectedDataRestored: true });
        }
      } else {
        this.setState({ isSelectedDataRestored: true });
      }
    }

    render() {
      const { hasInternalServerError, tryAgainClicked } = this.props;
      const { isSelectedDataRestored } = this.state;
      if (isSelectedDataRestored || hasInternalServerError || tryAgainClicked) {
        return <Component {...this.props} />;
      }

      return null;
    }
  };

const checkErrors = (errorMessages) =>
  containsMessage(
    errorMessages,
    errorTypes.SOMETHING_WENT_WRONG_ERROR,
    errorTypes.STILL_CANT_LOAD_THE_PAGE_ERROR,
  );

export const mapStateToProps = ({
  messages: {
    app: { errorMessages, tryAgainClicked },
  },
  editAppointment: { editMode },
  appointment: {
    draft: {
      appointmentType: { id: storedAppointmentTypeId },
    },
  },
}) => ({
  tryAgainClicked,
  hasInternalServerError: checkErrors(errorMessages),
  editMode,
  storedAppointmentTypeId,
});

const mapDispatchToProps = {
  dispatchRestoreAppointmentDraftSelectedData: appointmentDraftActions.restoreSelectedData,
  dispatchSaveDraftAppointmentType: appointmentDraftActions.saveDraftAppointmentType,
  dispatchRestoreJourneyStateSelectedData: journeyStateActions.restoreSelectedData,
  dispatchApiCallFinished: apiCallFinished,
  dispatchCountedApiCallFinished: countedApiCallFinished,
};

export const ConnectedPersistToken = (Component) =>
  connect(mapStateToProps, mapDispatchToProps)(persistToken(Component));
